import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles.css';

import { AdminShell } from './components/AdminShell.js';
import { LoginPage } from './routes/login.js';

// Code-split the route bodies — the dashboard pulls in Recharts (~heavy), so
// lazy-loading keeps the initial bundle small. Each route becomes its own chunk.
const DashboardPage = lazy(() =>
  import('./routes/dashboard.js').then((m) => ({ default: m.DashboardPage })),
);
const UsersPage = lazy(() => import('./routes/users.js').then((m) => ({ default: m.UsersPage })));
const TicketsPage = lazy(() =>
  import('./routes/tickets.js').then((m) => ({ default: m.TicketsPage })),
);
const AlarmsPage = lazy(() =>
  import('./routes/alarms.js').then((m) => ({ default: m.AlarmsPage })),
);
const AuditPage = lazy(() => import('./routes/audit.js').then((m) => ({ default: m.AuditPage })));
const InsightsPage = lazy(() =>
  import('./routes/insights.js').then((m) => ({ default: m.InsightsPage })),
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const lazyRoute = (el: React.ReactNode) => (
  <Suspense fallback={<div className="p-8 text-muted-foreground">Loading…</div>}>{el}</Suspense>
);

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: <AdminShell />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: lazyRoute(<DashboardPage />) },
      { path: 'users', element: lazyRoute(<UsersPage />) },
      { path: 'tickets', element: lazyRoute(<TicketsPage />) },
      { path: 'audit', element: lazyRoute(<AuditPage />) },
      { path: 'insights', element: lazyRoute(<InsightsPage />) },
      { path: 'alarms', element: lazyRoute(<AlarmsPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
